import React, { useState } from "react";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { object } from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import { InputField } from "../InputField";
import { isValidEmail } from "../../utils";
import { Toast } from "./Toast";
import { NEWSLETTER_API } from "../../../constants";
import { SvgIconHandler } from "../svg-icon-hadler";

import "./news-letter.m.css";

const NS_LIST = [
  { key: "Daily Updates", value: "211" },
  { key: "Finance", value: "204" },
  { key: "Business", value: "88" },
  { key: "Weekend", value: "78" },
  { key: "Sport", value: "206" },
  { key: "Ask Gulf News", value: "1038" },
  { key: "Luxury Travel", value: "1052" },
  { key: "Editor's Message", value: "1053" },
];

const Newsletter = ({ story = {}, customStyle, newsLetterTextsStyle, isButtonPositionLower, gridStyle }) => {
  const [email, setEmail] = useState("");
  const [selectedLists, setSelectedLists] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [toastStatus, setToastStatus] = useState("success");
  const [showToast, setShowToast] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showInvalidIcon, setShowInvalidIcon] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaSiteKey = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "google_recaptcha_site_key"], "")
  );

  const handleEmail = (event) => {
    setErrorMsg("");
    setShowInvalidIcon(false);
    const emailValue = event.target.value;
    setEmail(emailValue);

    // Check if email is valid
    if (emailValue && isValidEmail(emailValue)) {
      setIsEmailValid(true);
    } else {
      setIsEmailValid(false);
    }
  };

  const handleCheckboxChange = (value) => {
    setSelectedLists((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const emailInput = e.target.elements.email;

    if (emailInput.validity.valueMissing) {
      setErrorMsg("Please enter email address");
      setShowInvalidIcon(true);
      setIsEmailValid(false);
      return;
    } else if (emailInput.validity.typeMismatch || !isValidEmail(email)) {
      setErrorMsg("Please enter a valid email address");
      setShowInvalidIcon(true);
      setIsEmailValid(false);
      return;
    }

    if (selectedLists.length === 0) {
      setErrorMsg("Please select at least one newsletter");
      return;
    }

    setIsSubmitting(true);

    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(recaptchaSiteKey, { action: "submit" }).then((token) => {
        submitForm(email, token);
      });
    });
  };

  const submitForm = async (email, captchaResponse) => {
    const payload = {
      email: email,
      lists: selectedLists,
      captchaResponse: captchaResponse,
    };

    try {
      const response = await fetch(NEWSLETTER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.response === "success") {
        setIsSubscribed(true);
        setToastMessage("Thank you for subscribing to Gulf News!");
        setToastStatus("success");
      } else {
        setToastMessage("Something went wrong. Please try again.");
        setToastStatus("error");
      }
    } catch (error) {
      setToastMessage("Something went wrong. Please try again.");
      setToastStatus("error");
    }
    setEmail("");
    setSelectedLists([]);
    setIsEmailValid(false);
    setShowInvalidIcon(false);
    setIsSubmitting(false);
    setShowToast(true);
  };

  if (isSubscribed) {
    return (
      <div styleName="news-letter-wrapper" style={customStyle}>
        <div styleName="success-container">
          <h2 styleName="newsletter-heading success-heading">
            <span>Thank You for Signing Up</span>
            <span styleName="success-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#28a745" />
                <path
                  d="M8 12L11 15L16 9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </h2>
          <p styleName="success-text">Get ready to receive updates on what matters to you.</p>
        </div>
      </div>
    );
  }

  return (
    <div styleName="news-letter-wrapper" style={customStyle}>
      <h2 styleName="newsletter-heading" style={newsLetterTextsStyle}>
        <span>Get Updates on Topics You Choose</span>
        <SvgIconHandler type="envelope" width={40} height={32} />
      </h2>
      <form
        styleName={`signup-form ${isButtonPositionLower ? "signup-form-button-position" : ""}`}
        onSubmit={handleSubmit}
      >
        <div styleName="checkbox-grid" style={gridStyle || undefined}>
          {NS_LIST.map((newsletter) => (
            <label key={newsletter.value} styleName="checkbox-label">
              <input
                type="checkbox"
                value={newsletter.value}
                checked={selectedLists.includes(newsletter.value)}
                onChange={() => handleCheckboxChange(newsletter.value)}
              />
              <span>{newsletter.key}</span>
            </label>
          ))}
        </div>
        <div styleName="spacer" />
        <div styleName="form-row">
          <div>
            <div styleName="email-input-wrapper">
              <InputField
                id="email"
                value={email}
                type="email"
                placeholder="Email address"
                required
                onChange={handleEmail}
                styleName={isEmailValid ? "" : showInvalidIcon ? "invalid" : ""}
              />
              {isEmailValid && <div styleName="validation-icon show" />}
              {showInvalidIcon && <div styleName="validation-icon invalid show" />}
            </div>
            {errorMsg && <p styleName="error-message">{errorMsg}</p>}
            <div style={{ color: "#596E79", fontSize: "12px", marginTop: "8px" }}>
              By signing up, you agree to our{" "}
              <a target="_blank" href="/about-gulf-news/privacy-policy">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a target="_blank" href="/about-gulf-news/term-conditions">
                Terms of Use
              </a>
              .
            </div>
          </div>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "SUBMITTING..." : "GET UPDATES"}
          </button>
        </div>
      </form>

      {showToast && <Toast status={toastStatus} message={toastMessage} onClose={setShowToast} />}
      <ReCAPTCHA sitekey={recaptchaSiteKey} size="invisible" badge="bottomright" />
    </div>
  );
};

Newsletter.propTypes = {
  story: object,
};

export default Newsletter;
